/**
 * MathPanel - Clase principal para NimbusMath
 * Editor de ecuaciones que exporta MathML
 */

import { CATEGORIES } from './categories.js';
import { STYLES } from './styles.js';
import { convertLatexToMathMl } from 'mathlive';
import { MathMLToLaTeX } from 'mathml-to-latex';

// Colores predefinidos para fórmulas
const MATH_COLORS = [
    { value: '', label: 'Auto', color: '#000000' },
    { value: '#000000', label: 'Negro', color: '#000000' },
    { value: '#1a3d82', label: 'Azul Nimbus', color: '#1a3d82' },
    { value: '#2563eb', label: 'Azul', color: '#2563eb' },
    { value: '#dc2626', label: 'Rojo', color: '#dc2626' },
    { value: '#16a34a', label: 'Verde', color: '#16a34a' },
    { value: '#9333ea', label: 'Morado', color: '#9333ea' },
    { value: '#ea580c', label: 'Naranja', color: '#ea580c' },
    { value: '#0891b2', label: 'Cyan', color: '#0891b2' },
    { value: '#4b5563', label: 'Gris', color: '#4b5563' },
];

// Tamaños de fuente predefinidos
const MATH_SIZES = [
    { value: '14pt', label: '14 (Default)' },
    { value: '6pt', label: '6' },
    { value: '8pt', label: '8' },
    { value: '10pt', label: '10' },
    { value: '12pt', label: '12' },
    { value: '14pt', label: '14' },
    { value: '16pt', label: '16' },
    { value: '18pt', label: '18' },
    { value: '20pt', label: '20' },
    { value: '22pt', label: '22' },
    { value: '24pt', label: '24' },
    { value: '26pt', label: '26' },
    { value: '28pt', label: '28' },
    { value: '30pt', label: '30' },
];

// Familias de fuente
const MATH_FONTS = [
    { value: '', label: 'Por defecto' },
    { value: 'serif', label: 'Serif' },
    { value: 'sans-serif', label: 'Sans Serif' },
    { value: 'monospace', label: 'Monospace' },
];

// Estilos de fuente (Normal, Negritas, Itálica)
const MATH_FONT_STYLES = [
    { value: 'normal', label: 'Normal', mathvariant: 'normal' },
    { value: 'bold', label: 'Negritas', mathvariant: 'bold' },
    { value: 'italic', label: 'Itálica', mathvariant: 'italic' },
];

/**
 * Aplica las reglas de espaciado decimal a una operación vertical LaTeX existente.
 * Solo actúa si el LaTeX es una operación vertical (\frac{\begin{array}...)
 * y los números en las celdas NO tienen espaciado LaTeX ya aplicado manualmente.
 *
 * Reglas (mismas que NimbusMathIA):
 *   Operandos con punto decimal y menos decimales que el máximo: \;\: por cada faltante (Sit. 1/3)
 *   Operandos sin punto decimal: \;\:\, para 1.º y 2.º decimal + \;\: para extras + \; por el punto (Sit. 2/4)
 *   Resultado con más dígitos enteros que operandos: \; post por cada entero extra
 *   Resultado con más dígitos decimales que operandos: \;\: pre por cada decimal extra
 */
function ajustarEspaciadoVertical(latex) {
    if (!latex.includes('\\frac{') || !latex.includes('\\begin{array}')) return latex;

    // Extrae números en celdas: & [espacios opcionales] [número]
    const numRegex = /(&\s*(?:\\[;:,]\s*)*)((?:\d+(?:\\,)?)*\d+(?:\.\d+)?)/g;
    const entradas = [];
    let m;

    while ((m = numRegex.exec(latex)) !== null) {
        const prefix = m[1];
        const numRaw = m[2];
        const numLimpio = numRaw.replace(/\\,/g, '').replace(/\s/g, '');
        if (!numLimpio || isNaN(Number(numLimpio))) continue;

        const antesDelMatch = latex.substring(0, m.index);
        const esResultado =
            antesDelMatch.includes('\\end{array}') &&
            antesDelMatch.lastIndexOf('\\end{array}') > antesDelMatch.lastIndexOf('\\begin{array}');

        const tieneEspacioPost = /^(?:\\[;:,])+/.test(latex.substring(m.index + m[0].length));

        entradas.push({ inicio: m.index, fin: m.index + m[0].length, prefix, numRaw, num: numLimpio, esResultado, tieneEspacioPost });
    }

    // Si no hay números o alguno ya tiene espaciado post → no modificar
    if (entradas.length === 0 || entradas.some(e => e.tieneEspacioPost)) return latex;

    const contarDec = s => { const idx = s.indexOf('.'); return idx === -1 ? 0 : s.length - idx - 1; };

    const ops = entradas.filter(e => !e.esResultado);
    if (ops.length === 0) return latex;

    const maxDecOp = Math.max(...ops.map(e => contarDec(e.num)));
    const maxIntOp  = Math.max(...ops.map(e => e.num.split('.')[0].length));
    const maxDec   = Math.max(...entradas.map(e => contarDec(e.num)));

    // Calcula el padding post para decimales faltantes (Sit. 1-4)
    const calcPadPost = (numStr, maxD, hasPoint) => {
        const nd = contarDec(numStr);
        if (maxD === 0 || nd >= maxD) return '';
        if (hasPoint) return '\\;\\:'.repeat(maxD - nd);          // Sit. 1/3
        let p = '';
        for (let i = 0; i < maxD; i++) p += i < 2 ? '\\;\\:\\,' : '\\;\\:'; // Sit. 2/4
        return p + '\\;'; // \; por el punto decimal
    };

    let out = latex;
    for (let i = entradas.length - 1; i >= 0; i--) {
        const e = entradas[i];
        const hasPoint = e.num.includes('.');
        let preExtra = '';
        let postPad  = '';

        if (e.esResultado) {
            const [enteraR, decimalR = ''] = e.num.split('.');
            postPad = calcPadPost(e.num, maxDecOp, hasPoint);
            // Regla 1: más dígitos enteros que operandos → \; post por cada extra
            const extraEnt = enteraR.length - maxIntOp;
            if (extraEnt > 0) postPad += '\\;'.repeat(extraEnt);
            // Regla 2: más decimales que operandos → \;\: pre por cada decimal extra
            const extraDec = decimalR.length - maxDecOp;
            if (extraDec > 0) preExtra = '\\;\\:'.repeat(extraDec);
        } else {
            postPad = calcPadPost(e.num, maxDec, hasPoint);
        }

        out = out.substring(0, e.inicio) + e.prefix + preExtra + e.numRaw + postPad + out.substring(e.fin);
    }

    return out;
}

export class MathPanel {
    constructor(options = {}) {
        this.options = {
            position: 'bottom-right',
            offset: { x: 16, y: 20 },
            draggable: true,
            minimizable: true,
            onInsert: null,
            onClose: null,
            enableHandwriting: true,
            openaiApiKey: null,
            mathsize: null,
            mathcolor: null,
            mathfont: null,
            ...options
        };
        
        this.container = null;
        this.mathfield = null;
        this.currentCategory = 'general';
        this.isMinimized = false;
        this.isDragging = false;
        this.dragOffset = { x: 0, y: 0 };
        this.isHandwritingMode = false;
        this.signaturePad = null;
        this.isRecognizing = false;
        this.isMathMLMode = false;
        
        // Estados de estilo actuales - defaults: negro, 22pt, monospace, normal
        this.currentColor = this.options.mathcolor || '#000000';
        this.currentSize = this.options.mathsize || '14pt';
        this.currentFont = this.options.mathfont || 'monospace';
        this.currentFontStyle = this.options.mathfontstyle || 'normal';
        
        this._injectStyles();
        this._loadSignaturePad();
    }
    
    async _loadSignaturePad() {
        if (typeof SignaturePad !== 'undefined') return;
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/signature_pad@4.1.7/dist/signature_pad.umd.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }
    
    _injectStyles() {
        if (document.getElementById('mathpanel-styles')) return;
        const style = document.createElement('style');
        style.id = 'mathpanel-styles';
        style.textContent = STYLES;
        document.head.appendChild(style);
    }
    
    _createUI() {
        this.container = document.createElement('div');
        this.container.className = 'mathpanel-container';
        this.container.innerHTML = this._generateHTML();
        document.body.appendChild(this.container);
        this._setupPosition();
        this._bindEvents();
        this._renderCategory('general');
    }
    
    _generateHTML() {
        // Generar botones de categorías para el SIDEBAR (vertical)
        const cats = Object.entries(CATEGORIES).map(([k, c]) => `
            <button class="mathpanel-sidebar-btn ${k === 'general' ? 'active' : ''}" 
                    data-category="${k}" title="${c.label}">
                <span class="mathpanel-sidebar-icon">${c.icon}</span>
                <span class="mathpanel-sidebar-label">${c.label}</span>
            </button>`).join('');
        
        // Generar opciones de color como select
        const colorOptions = MATH_COLORS.map(c => 
            `<option value="${c.value}" ${c.value === this.currentColor ? 'selected' : ''} style="color: ${c.color}">${c.label}</option>`
        ).join('');
        
        // Generar opciones de tamaño
        const sizeOptions = MATH_SIZES.map(s => 
            `<option value="${s.value}" ${s.value === this.currentSize ? 'selected' : ''}>${s.label}</option>`
        ).join('');
        
        // Generar opciones de fuente
        const fontOptions = MATH_FONTS.map(f => 
            `<option value="${f.value}" ${f.value === this.currentFont ? 'selected' : ''}>${f.label}</option>`
        ).join('');
        
        // Generar opciones de estilo de fuente (Normal, Negritas, Itálica)
        const fontStyleOptions = MATH_FONT_STYLES.map(fs => 
            `<option value="${fs.value}" ${fs.value === this.currentFontStyle ? 'selected' : ''}>${fs.label}</option>`
        ).join('');
        
        return `
            <div class="mathpanel">
                <div class="mathpanel-header">
                    <div class="mathpanel-title"><span>∑</span><span>Nimbus Math</span></div>
                    <div class="mathpanel-controls">
                        <button class="mathpanel-control-btn handwriting" title="Dibujar (próximamente)" disabled style="opacity:0.4;cursor:not-allowed;">✏</button>
                        <button class="mathpanel-control-btn minimize" title="Minimizar">─</button>
                        <button class="mathpanel-control-btn close" title="Cerrar">×</button>
                    </div>
                </div>
                <div class="mathpanel-body">
                    <!-- NUEVO: Container con Sidebar + Panel Principal -->
                    <div class="mathpanel-layout">
                        <!-- SIDEBAR IZQUIERDO -->
                        <div class="mathpanel-sidebar">
                            ${cats}
                        </div>
                        
                        <!-- PANEL PRINCIPAL (derecha) -->
                        <div class="mathpanel-main">
                            <!-- Barra de estilos -->
                            <div class="mathpanel-styles-bar">
                                <div class="mathpanel-style-group">
                                    <label class="mathpanel-style-label">Color:</label>
                                    <select class="mathpanel-style-select mathpanel-color-select" id="mathpanel-color-select">
                                        ${colorOptions}
                                    </select>
                                </div>
                                <div class="mathpanel-style-group">
                                    <label class="mathpanel-style-label">Tamaño:</label>
                                    <select class="mathpanel-style-select" id="mathpanel-size-select">
                                        ${sizeOptions}
                                    </select>
                                </div>
                                <div class="mathpanel-style-group">
                                    <label class="mathpanel-style-label">Fuente:</label>
                                    <select class="mathpanel-style-select" id="mathpanel-font-select">
                                        ${fontOptions}
                                    </select>
                                </div>
                                <div class="mathpanel-style-group">
                                    <label class="mathpanel-style-label">Estilo:</label>
                                    <select class="mathpanel-style-select" id="mathpanel-fontstyle-select">
                                        ${fontStyleOptions}
                                    </select>
                                </div>
                            </div>
                            
                            <!-- Tabs -->
                            <div class="mathpanel-tabs" id="mathpanel-tabs"></div>
                            
                            <!-- Símbolos -->
                            <div class="mathpanel-symbols">
                                <div class="mathpanel-symbols-grid" id="mathpanel-symbols-grid"></div>
                            </div>
                            
                            <!-- Handwriting area (oculto por defecto) -->
                            <div class="mathpanel-handwriting-area">
                                <div class="mathpanel-canvas-container">
                                    <canvas class="mathpanel-canvas" id="mathpanel-canvas"></canvas>
                                    <div class="mathpanel-canvas-placeholder" id="mathpanel-canvas-placeholder">✏️ Dibuja aquí</div>
                                </div>
                                <div class="mathpanel-hw-toolbar">
                                    <button class="mathpanel-hw-btn" id="mathpanel-hw-clear">🗑️</button>
                                    <button class="mathpanel-hw-btn" id="mathpanel-hw-undo">↩️</button>
                                    <div class="mathpanel-hw-loading" id="mathpanel-hw-loading">
                                        <div class="mathpanel-hw-spinner"></div>
                                    </div>
                                    <button class="mathpanel-hw-btn recognize" id="mathpanel-hw-recognize" disabled>🔍</button>
                                </div>
                            </div>
                            
                            <!-- Editor section -->
                            <div class="mathpanel-editor-section">
                                <div class="mathpanel-mathfield-wrapper">
                                    <div class="mathpanel-toolbar">
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-matrix-btn" title="Insertar matriz/array">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <rect x="2" y="2" width="5" height="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
                                                <rect x="9" y="2" width="5" height="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
                                                <rect x="2" y="9" width="5" height="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
                                                <rect x="9" y="9" width="5" height="5" fill="none" stroke="currentColor" stroke-width="1.5"/>
                                            </svg>
                                        </button>
                                        <div class="mathpanel-toolbar-separator"></div>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-frac-btn" title="Fracción (selección como numerador)">
                                            <span style="font-size: 14px; line-height: 1;">⁄</span>
                                        </button>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-sqrt-btn" title="Raíz cuadrada de selección">
                                            <span style="font-size: 14px;">√</span>
                                        </button>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-paren-btn" title="Paréntesis alrededor de selección">
                                            <span style="font-size: 12px;">()</span>
                                        </button>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-underline-btn" title="Línea debajo de selección">
                                            <span style="font-size: 12px; text-decoration: underline;">a̲</span>
                                        </button>
                                        <div class="mathpanel-toolbar-separator"></div>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-phantom-small-btn" title="Espacio pequeño (\\,)">
                                            <span style="font-size: 10px;">p</span>
                                        </button>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-phantom-large-btn" title="Espacio grande (\\quad)">
                                            <span style="font-size: 11px;">G</span>
                                        </button>
                                        <div class="mathpanel-toolbar-separator"></div>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-selectall-btn" title="Seleccionar todo">
                                            <span style="font-size: 11px;">⌘A</span>
                                        </button>
                                        <div class="mathpanel-toolbar-separator"></div>
                                        <button class="mathpanel-toolbar-btn" id="mathpanel-mathml-toggle-btn" title="Cambiar a modo MathML">
                                            <span style="font-size: 11px; font-weight: bold;">&lt;/&gt;</span>
                                        </button>
                                    </div>
                                    <math-field id="mathpanel-mathfield" virtual-keyboard-mode="off"></math-field>
                                    <textarea id="mathpanel-mathml-input" class="mathpanel-mathml-input" placeholder="Pega tu código MathML aquí..." style="display: none;"></textarea>
                                </div>
                                <div class="mathpanel-actions">
                                    <button class="mathpanel-btn mathpanel-btn-secondary" id="mathpanel-clear">Limpiar</button>
                                    <button class="mathpanel-btn mathpanel-btn-primary" id="mathpanel-insert">Insertar</button>
                                </div>
                            </div>
                            
                            <!-- Selector de matrices -->
                            <div class="mathpanel-matrix-selector" id="mathpanel-matrix-selector" style="display: none;">
                                <div class="mathpanel-matrix-grid" id="mathpanel-matrix-grid"></div>
                                <div class="mathpanel-matrix-label" id="mathpanel-matrix-label">1 × 1</div>
                                <div class="mathpanel-matrix-options">
                                    <label class="mathpanel-matrix-option">
                                        <input type="checkbox" id="mathpanel-matrix-hline" />
                                        <span>Agregar línea al final (para resultado)</span>
                                    </label>
                                    <div style="display: flex; gap: 8px; align-items: center; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e3e8;">
                                        <span style="font-size: 12px; color: #666;">Tamaño personalizado:</span>
                                        <input type="number" id="mathpanel-matrix-custom-rows" min="1" max="20" value="3" style="width: 50px; padding: 4px; border: 1px solid #ddd; border-radius: 3px; font-size: 12px;" placeholder="Filas" />
                                        <span style="font-size: 12px;">×</span>
                                        <input type="number" id="mathpanel-matrix-custom-cols" min="1" max="20" value="3" style="width: 50px; padding: 4px; border: 1px solid #ddd; border-radius: 3px; font-size: 12px;" placeholder="Cols" />
                                        <button id="mathpanel-matrix-custom-btn" style="padding: 4px 8px; background: #1a3d82; color: white; border: none; border-radius: 3px; cursor: pointer; font-size: 11px;">Crear</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <button class="mathpanel-minimized-btn"><span>∑</span><span>Nimbus Math</span></button>`;
    }
    
    _setupPosition() {
        const { position: pos, offset } = this.options;
        const s = this.container.style;
        if (pos.includes('bottom')) s.bottom = offset.y + 'px';
        else s.top = offset.y + 'px';
        if (pos.includes('right')) s.right = offset.x + 'px';
        else s.left = offset.x + 'px';
    }
    
    _bindEvents() {
        this.mathfield = this.container.querySelector('#mathpanel-mathfield');
        
        // Configurar mathfield para comportamiento similar al demo de mathlive.io
        // Usar propiedades directas en lugar de setOptions (deprecado)
        if (this.mathfield) {
            // Propiedades de comportamiento
            this.mathfield.smartMode = true;  // Permite modo texto y matemáticas
            this.mathfield.smartFence = true;
            this.mathfield.smartSuperscript = true;
            this.mathfield.removeExtraneousParentheses = false;
            this.mathfield.readOnly = false;
            
            // Permitir selección libre - NO navegar automáticamente a placeholders
            // El usuario puede hacer clic donde quiera y seleccionar libremente
            
            // Filtrar el menú contextual de MathLive para mostrar solo las opciones deseadas
            // IDs permitidos: variant (Estilo de fuente), color, select-all
            const allowedIds = ['variant', 'color', 'select-all'];
            const defaultMenuItems = this.mathfield.menuItems;
            if (defaultMenuItems && Array.isArray(defaultMenuItems)) {
                this.mathfield.menuItems = defaultMenuItems.filter(item => {
                    if (!item) return false;
                    if (item.type === 'divider') return false; // Quitar separadores
                    // Mantener solo los items con IDs permitidos
                    return item.id && allowedIds.includes(item.id);
                });
            }
        }
        
        this.container.querySelectorAll('.mathpanel-sidebar-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.container.querySelectorAll('.mathpanel-sidebar-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this._renderCategory(btn.dataset.category);
            });
        });
        
        this.container.querySelector('.mathpanel-control-btn.minimize').addEventListener('click', () => this.toggleMinimize());
        this.container.querySelector('.mathpanel-control-btn.close').addEventListener('click', () => this.close());
        this.container.querySelector('.mathpanel-control-btn.handwriting')?.addEventListener('click', () => this._toggleHandwritingMode());
        this.container.querySelector('#mathpanel-clear').addEventListener('click', () => this.clear());
        this.container.querySelector('#mathpanel-insert').addEventListener('click', () => this._insertFormula());
        
        // Eventos de estilos
        this._bindStyleEvents();
        
        // Eventos de operaciones de toolbar
        this._bindToolbarOperations();
        
        // Eventos del selector de matrices
        this._bindMatrixSelectorEvents();
        
        if (this.options.draggable) this._setupDrag();
        
        this.mathfield.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this._insertFormula();
            }
            
            // Enter sin modificadores: insertar salto de línea (\\)
            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                e.preventDefault();
                this.mathfield.executeCommand(['insert', '\\\\']);
            }
            
            // Interceptar espacio para insertar espacio matemático visible
            // En modo math, los espacios normales son ignorados
            if (e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                // Insertar un espacio matemático medio (\;) que es visible
                // Alternativas: \quad (grande), \; (medio), \, (pequeño), ~ (non-breaking)
                this.mathfield.executeCommand(['insert', '\\;']);
            }
        });
    }
    
    _bindStyleEvents() {
        // Color select
        const colorSelect = this.container.querySelector('#mathpanel-color-select');
        colorSelect?.addEventListener('change', (e) => {
            this.currentColor = e.target.value;
            this._updateMathfieldStyles();
        });
        
        // Size select
        const sizeSelect = this.container.querySelector('#mathpanel-size-select');
        sizeSelect?.addEventListener('change', (e) => {
            this.currentSize = e.target.value;
            this._updateMathfieldStyles();
        });
        
        // Font select
        const fontSelect = this.container.querySelector('#mathpanel-font-select');
        fontSelect?.addEventListener('change', (e) => {
            this.currentFont = e.target.value;
            this._updateMathfieldStyles();
        });
        
        // Font style select (Normal, Negritas, Itálica)
        const fontStyleSelect = this.container.querySelector('#mathpanel-fontstyle-select');
        fontStyleSelect?.addEventListener('change', (e) => {
            this.currentFontStyle = e.target.value;
            this._updateMathfieldStyles();
        });
    }
    
    _updateMathfieldStyles() {
        if (!this.mathfield) return;
        
        // Aplicar estilos CSS básicos al mathfield para preview visual
        const styles = [];
        if (this.currentColor) styles.push(`color: ${this.currentColor}`);
        if (this.currentSize) styles.push(`font-size: ${this.currentSize}`);
        if (this.currentFont) styles.push(`font-family: ${this.currentFont}`);
        
        // Aplicar estilo de fuente (bold/italic) como CSS para preview
        if (this.currentFontStyle === 'bold') {
            styles.push('font-weight: bold');
            styles.push('font-style: normal');
        } else if (this.currentFontStyle === 'italic') {
            styles.push('font-weight: normal');
            styles.push('font-style: italic');
        } else {
            styles.push('font-weight: normal');
            styles.push('font-style: normal');
        }
        
        this.mathfield.style.cssText = styles.join('; ');
    }
    
    _bindToolbarOperations() {
        // Botón de fracción - envuelve selección como numerador
        this.container.querySelector('#mathpanel-frac-btn')?.addEventListener('click', () => {
            const selection = this.mathfield.getValue(this.mathfield.selection, 'latex');
            if (selection && selection.trim()) {
                // Hay selección, usarla como numerador
                this.mathfield.executeCommand(['insert', `\\frac{${selection}}{\\placeholder{}}`, { selectionMode: 'after' }]);
            } else {
                // Sin selección, insertar fracción vacía
                this.mathfield.executeCommand(['insert', '\\frac{\\placeholder{}}{\\placeholder{}}']);
            }
            this.mathfield.focus();
            setTimeout(() => this.mathfield.executeCommand('moveToNextPlaceholder'), 50);
        });
        
        // Botón de raíz cuadrada
        this.container.querySelector('#mathpanel-sqrt-btn')?.addEventListener('click', () => {
            const selection = this.mathfield.getValue(this.mathfield.selection, 'latex');
            if (selection && selection.trim()) {
                this.mathfield.executeCommand(['insert', `\\sqrt{${selection}}`, { selectionMode: 'after' }]);
            } else {
                this.mathfield.executeCommand(['insert', '\\sqrt{\\placeholder{}}']);
            }
            this.mathfield.focus();
        });
        
        // Botón de paréntesis
        this.container.querySelector('#mathpanel-paren-btn')?.addEventListener('click', () => {
            const selection = this.mathfield.getValue(this.mathfield.selection, 'latex');
            if (selection && selection.trim()) {
                this.mathfield.executeCommand(['insert', `\\left(${selection}\\right)`, { selectionMode: 'after' }]);
            } else {
                this.mathfield.executeCommand(['insert', '\\left(\\placeholder{}\\right)']);
            }
            this.mathfield.focus();
        });
        
        // Botón de línea debajo (underline) - útil para operaciones verticales
        this.container.querySelector('#mathpanel-underline-btn')?.addEventListener('click', () => {
            const selection = this.mathfield.getValue(this.mathfield.selection, 'latex');
            if (selection && selection.trim()) {
                // Envolver selección en una fracción con denominador vacío para crear línea
                this.mathfield.executeCommand(['insert', `\\frac{${selection}}{\\placeholder{}}`, { selectionMode: 'after' }]);
            } else {
                // Sin selección, tomar todo el contenido
                const allContent = this.mathfield.getValue('latex');
                if (allContent && allContent.trim()) {
                    this.mathfield.setValue(`\\frac{${allContent}}{\\placeholder{}}`);
                } else {
                    this.mathfield.executeCommand(['insert', '\\frac{\\placeholder{}}{\\placeholder{}}']);
                }
            }
            this.mathfield.focus();
            setTimeout(() => this.mathfield.executeCommand('moveToNextPlaceholder'), 50);
        });
        
        // Botón de espacio pequeño (usa \, que es un espacio fino visible)
        this.container.querySelector('#mathpanel-phantom-small-btn')?.addEventListener('click', () => {
            // \, es un espacio fino (3/18 em), visible en el render
            this.mathfield.executeCommand(['insert', '\\,']);
            this.mathfield.focus();
        });
        
        // Botón de espacio grande (usa \quad que es un espacio grande visible)
        this.container.querySelector('#mathpanel-phantom-large-btn')?.addEventListener('click', () => {
            // \quad es un espacio de 1em, visible en el render
            this.mathfield.executeCommand(['insert', '\\quad']);
            this.mathfield.focus();
        });
        
        // Botón de seleccionar todo
        this.container.querySelector('#mathpanel-selectall-btn')?.addEventListener('click', () => {
            this.mathfield.executeCommand('selectAll');
            this.mathfield.focus();
        });
        
        // Botón de toggle MathML
        const mathmlToggleBtn = this.container.querySelector('#mathpanel-mathml-toggle-btn');
        const mathmlInput = this.container.querySelector('#mathpanel-mathml-input');
        
        mathmlToggleBtn?.addEventListener('click', () => {
            this.isMathMLMode = !this.isMathMLMode;
            
            if (this.isMathMLMode) {
                // Cambiar a modo MathML (textarea)
                this.mathfield.style.display = 'none';
                mathmlInput.style.display = 'block';
                mathmlToggleBtn.classList.add('active');
                mathmlToggleBtn.title = 'Cambiar a modo visual';
                mathmlInput.focus();
            } else {
                this._applyMathMLFromTextarea(mathmlInput, mathmlToggleBtn);
            }
        });
        
        // Auto-switch: al pegar MathML en el textarea, convertir y cambiar a modo visual
        mathmlInput?.addEventListener('paste', (e) => {
            if (!this.isMathMLMode) return;
            // Esperar a que el contenido se pegue
            setTimeout(() => {
                const content = mathmlInput.value.trim();
                if (content && /<math[^>]*>/i.test(content)) {
                    this._applyMathMLFromTextarea(mathmlInput, mathmlToggleBtn);
                }
            }, 100);
        });
        
        // Botón de cambio de modo (math <-> texto/LaTeX)
        const modeBtn = this.container.querySelector('#mathpanel-mode-btn');
        const latexInput = this.container.querySelector('#mathpanel-latex-input');
        this.isLatexMode = false;
        this.pendingMathML = null; // Para almacenar MathML si se detecta
        
        modeBtn?.addEventListener('click', () => {
            this.isLatexMode = !this.isLatexMode;
            
            if (this.isLatexMode) {
                // Cambiar a modo texto/LaTeX
                const currentLatex = this.mathfield.getValue('latex');
                latexInput.value = currentLatex;
                this.mathfield.style.display = 'none';
                latexInput.style.display = 'block';
                modeBtn.classList.add('active');
                modeBtn.title = 'Cambiar a modo visual';
                modeBtn.querySelector('span').textContent = '∑';
                latexInput.focus();
            } else {
                // Cambiar a modo math visual
                this._processTextInput(latexInput.value.trim());
                latexInput.style.display = 'none';
                this.mathfield.style.display = 'block';
                modeBtn.classList.remove('active');
                modeBtn.title = 'Cambiar a modo texto (para pegar LaTeX/MathML)';
                modeBtn.querySelector('span').textContent = '𝑓(x)';
                this.mathfield.focus();
            }
        });
    }
    
    // Procesar entrada de texto (detectar si es LaTeX o MathML)
    _processTextInput(input) {
        if (!input) return;
        
        // Detectar si es MathML (comienza con <math> o <math xmlns=...)
        const isMathML = /^\s*<math(\s|>|xmlns)/i.test(input);
        
        if (isMathML) {
            // Guardar el MathML para usarlo directamente al insertar
            this.pendingMathML = input;
            // Intentar convertir MathML a LaTeX para mostrar en el mathfield
            // MathLive no tiene conversión directa, así que mostramos un placeholder
            this.mathfield.setValue('\\text{[MathML cargado - listo para insertar]}');
        } else {
            this.pendingMathML = null;
            this.mathfield.setValue(input);
        }
    }
    
    _bindMatrixSelectorEvents() {
        const matrixBtn = this.container.querySelector('#mathpanel-matrix-btn');
        const matrixSelector = this.container.querySelector('#mathpanel-matrix-selector');
        const matrixGrid = this.container.querySelector('#mathpanel-matrix-grid');
        const matrixLabel = this.container.querySelector('#mathpanel-matrix-label');
        const hlineCheckbox = this.container.querySelector('#mathpanel-matrix-hline');
        
        // Generar grid de 10x10
        const maxRows = 10;
        const maxCols = 10;
        for (let i = 0; i < maxRows * maxCols; i++) {
            const cell = document.createElement('div');
            cell.className = 'mathpanel-matrix-cell';
            cell.dataset.row = Math.floor(i / maxCols);
            cell.dataset.col = i % maxCols;
            matrixGrid.appendChild(cell);
        }
        
        // Toggle selector al hacer clic en el botón
        matrixBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const isVisible = matrixSelector.style.display !== 'none';
            matrixSelector.style.display = isVisible ? 'none' : 'block';
            
            if (!isVisible) {
                // Posicionar el selector cerca del botón
                const btnRect = matrixBtn.getBoundingClientRect();
                const containerRect = this.container.getBoundingClientRect();
                matrixSelector.style.top = (btnRect.bottom - containerRect.top + 5) + 'px';
                matrixSelector.style.left = (btnRect.left - containerRect.left) + 'px';
            }
        });
        
        // Cerrar selector al hacer clic fuera
        document.addEventListener('click', (e) => {
            if (!matrixSelector.contains(e.target) && e.target !== matrixBtn) {
                matrixSelector.style.display = 'none';
            }
        });
        
        // Hover sobre celdas
        let selectedRows = 1, selectedCols = 1;
        matrixGrid.addEventListener('mouseover', (e) => {
            if (e.target.classList.contains('mathpanel-matrix-cell')) {
                const row = parseInt(e.target.dataset.row);
                const col = parseInt(e.target.dataset.col);
                selectedRows = row + 1;
                selectedCols = col + 1;
                
                // Actualizar label
                matrixLabel.textContent = `${selectedRows} × ${selectedCols}`;
                
                // Highlight celdas
                const cells = matrixGrid.querySelectorAll('.mathpanel-matrix-cell');
                cells.forEach(cell => {
                    const cellRow = parseInt(cell.dataset.row);
                    const cellCol = parseInt(cell.dataset.col);
                    if (cellRow < selectedRows && cellCol < selectedCols) {
                        cell.classList.add('active');
                    } else {
                        cell.classList.remove('active');
                    }
                });
            }
        });
        
        // Click en celda para insertar matriz
        matrixGrid.addEventListener('click', (e) => {
            if (e.target.classList.contains('mathpanel-matrix-cell')) {
                const includeHline = hlineCheckbox.checked;
                this._insertMatrix(selectedRows, selectedCols, includeHline);
                matrixSelector.style.display = 'none';
            }
        });
        
        // Botón de tamaño personalizado
        const customBtn = this.container.querySelector('#mathpanel-matrix-custom-btn');
        const customRowsInput = this.container.querySelector('#mathpanel-matrix-custom-rows');
        const customColsInput = this.container.querySelector('#mathpanel-matrix-custom-cols');
        
        customBtn.addEventListener('click', () => {
            const rows = parseInt(customRowsInput.value) || 3;
            const cols = parseInt(customColsInput.value) || 3;
            const includeHline = hlineCheckbox.checked;
            
            // Validar límites
            const validRows = Math.max(1, Math.min(20, rows));
            const validCols = Math.max(1, Math.min(20, cols));
            
            this._insertMatrix(validRows, validCols, includeHline);
            matrixSelector.style.display = 'none';
        });
    }
    
    _insertMatrix(rows, cols, includeHline) {
        // Generar LaTeX para array sin delimitadores
        const alignment = 'r'.repeat(cols); // Alinear a la derecha
        let latex = `\\begin{array}{${alignment}}\n`;
        
        // Generar filas
        for (let i = 0; i < rows; i++) {
            const row = Array(cols).fill('\\placeholder{}').join(' & ');
            latex += `  ${row}`;
            if (i < rows - 1) latex += ' \\\\';
            latex += '\n';
        }
        
        // Agregar hline y fila de resultado si está marcado
        if (includeHline) {
            latex += '  \\hline \\\\\n';
            const resultRow = Array(cols).fill('\\placeholder{}').join(' & ');
            latex += `  ${resultRow}\n`;
        }
        
        latex += '\\end{array}';
        
        // Insertar en mathfield
        this.mathfield.executeCommand(['insert', latex]);
        this.mathfield.focus();
        
        // Mover al primer placeholder
        setTimeout(() => this.mathfield.executeCommand('moveToNextPlaceholder'), 50);
    }
    
    _setupDrag() {
        const header = this.container.querySelector('.mathpanel-header');
        header.addEventListener('mousedown', (e) => {
            if (e.target.closest('.mathpanel-control-btn')) return;
            this.isDragging = true;
            const rect = this.container.getBoundingClientRect();
            this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };
            this.container.style.top = rect.top + 'px';
            this.container.style.left = rect.left + 'px';
            this.container.style.right = 'auto';
            this.container.style.bottom = 'auto';
            document.addEventListener('mousemove', this._onDrag);
            document.addEventListener('mouseup', this._onDragEnd);
        });
        
        this._onDrag = (e) => {
            if (!this.isDragging) return;
            const panel = this.container.querySelector('.mathpanel').getBoundingClientRect();
            let x = Math.max(0, Math.min(e.clientX - this.dragOffset.x, window.innerWidth - panel.width - 16));
            let y = Math.max(0, Math.min(e.clientY - this.dragOffset.y, window.innerHeight - panel.height - 20));
            this.container.style.left = x + 'px';
            this.container.style.top = y + 'px';
        };
        
        this._onDragEnd = () => {
            this.isDragging = false;
            document.removeEventListener('mousemove', this._onDrag);
            document.removeEventListener('mouseup', this._onDragEnd);
        };
    }
    
    _renderCategory(key) {
        this.currentCategory = key;
        const cat = CATEGORIES[key];
        const tabsEl = this.container.querySelector('#mathpanel-tabs');
        const tabKeys = Object.keys(cat.tabs);
        
        tabsEl.innerHTML = tabKeys.map((k, i) => `
            <button class="mathpanel-tab-btn ${i === 0 ? 'active' : ''}" data-tab="${k}">${cat.tabs[k].label}</button>
        `).join('');
        
        tabsEl.querySelectorAll('.mathpanel-tab-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                tabsEl.querySelectorAll('.mathpanel-tab-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this._renderSymbols(key, btn.dataset.tab);
            });
        });
        
        this._renderSymbols(key, tabKeys[0]);
    }
    
    _renderSymbols(catKey, tabKey) {
        const symbols = CATEGORIES[catKey].tabs[tabKey].symbols;
        const grid = this.container.querySelector('#mathpanel-symbols-grid');
        
        // Detectar si es categoría de operaciones verticales para aplicar estilos especiales
        const isVerticalOp = catKey === 'operaciones_verticales';
        
        grid.innerHTML = symbols.map(s => `
            <button class="mathpanel-symbol-btn ${s.small ? 'small' : ''} ${s.wide ? 'wide' : ''} ${isVerticalOp ? 'vertical-op' : ''}"
                    data-latex="${s.latex.replace(/"/g, '&quot;')}" title="${s.title}">${s.display}</button>
        `).join('');
        
        grid.querySelectorAll('.mathpanel-symbol-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.mathfield.executeCommand(['insert', btn.dataset.latex]);
                this.mathfield.focus();
                if (btn.dataset.latex.includes('\\placeholder')) {
                    setTimeout(() => this.mathfield.executeCommand('moveToNextPlaceholder'), 50);
                }
            });
        });
    }
    
    _insertFormula() {
        // Si está en modo LaTeX/MathML, primero procesar el input
        const latexInput = this.container.querySelector('#mathpanel-latex-input');
        if (this.isLatexMode && latexInput) {
            const inputCode = latexInput.value.trim();
            if (inputCode) {
                this._processTextInput(inputCode);
            }
        }
        
        // Si hay MathML pendiente (pegado directamente), usarlo
        if (this.pendingMathML) {
            const mathml = this.pendingMathML;
            // Para MathML directo, no tenemos LaTeX original
            const latex = '';
            
            if (this.options.onInsert) {
                this.options.onInsert(mathml, latex);
            }
            this.pendingMathML = null;
            this.close();
            return;
        }
        
        const latex = this.mathfield.value.trim();
        if (!latex) return;
        
        // Limpiar placeholders vacíos del LaTeX
        // Reemplazar con \;\; (dos espacios gruesos) para alinear con operadores (+/-)
        let cleanLatex = latex
            .replace(/\\placeholder\s*\{[^}]*\}/g, '\\;\\;')  // Reemplazar placeholders con dos espacios gruesos
            .replace(/\\phantom\s*\{[^}]*\}/g, '\\;\\;')  // Reemplazar phantom con dos espacios gruesos
            .replace(/\{\}\s*&/g, '\\;\\;&')  // Agregar espacio antes de & vacío
            .replace(/&\s*\{\}/g, '&\\;\\;')  // Agregar espacio después de & vacío
            .replace(/\{\}\s*\\\\/g, '\\;\\;\\\\')  // Agregar espacio antes de \\ vacío
            .replace(/\\\\\s*\{\}/g, '\\\\\\;\\;')  // Agregar espacio después de \\ vacío
            .replace(/\{\}/g, '\\;\\;')  // Reemplazar grupos vacíos con dos espacios gruesos
            // Limpiar espacios múltiples consecutivos
            .replace(/\\;\\;\s*\\;\\;/g, '\\;\\;');

        // Ajustar espaciado decimal en operaciones verticales (si no hay espaciado manual)
        cleanLatex = ajustarEspaciadoVertical(cleanLatex);

        let mathml = '';
        
        try {
            // Generar MathML desde el LaTeX LIMPIO usando MathLive
            // Esto evita que los placeholders aparezcan en el MathML
            mathml = convertLatexToMathMl(cleanLatex);
            
            // Limpiar placeholders del MathML
            // Reemplazar celdas vacías con espacio para evitar vista negra
            mathml = mathml
                // Remover elementos con data-placeholder pero mantener estructura
                .replace(/<[^>]*data-placeholder[^>]*>.*?<\/[^>]+>/gi, '<mspace width="0.2em"/>')
                // Reemplazar caracteres de cuadro Unicode con espacio
                .replace(/<mi[^>]*>[\u25A0-\u25FF\u2B1C]<\/mi>/gi, '<mspace width="0.2em"/>')
                .replace(/<mo[^>]*>[\u25A0-\u25FF\u2B1C]<\/mo>/gi, '<mspace width="0.2em"/>')
                .replace(/<mtext[^>]*>[\u25A0-\u25FF\u2B1C]<\/mtext>/gi, '<mspace width="0.2em"/>')
                // Remover caracteres de cuadro sueltos
                .replace(/[\u25A0-\u25FF]/g, '')
                .replace(/\u2B1C/g, '')
                // Agregar espacio a mtd vacíos para evitar vista negra
                .replace(/<mtd>\s*<\/mtd>/gi, '<mtd><mspace width="0.2em"/></mtd>')
                .replace(/<mtd\/>/gi, '<mtd><mspace width="0.2em"/></mtd>')
                // Agregar espacio a mrow vacíos
                .replace(/<mrow>\s*<\/mrow>/gi, '<mrow><mspace width="0.2em"/></mrow>')
                .replace(/<mrow\/>/gi, '<mrow><mspace width="0.2em"/></mrow>');
            
            mathml = MathPanel.formatMathML(mathml, {
                mathsize: this.currentSize || this.options.mathsize,
                mathcolor: this.currentColor || this.options.mathcolor,
                mathfont: this.currentFont || this.options.mathfont,
                mathfontstyle: this.currentFontStyle || 'normal'
            });
        } catch (e) {
            console.warn('Error generando MathML:', e);
        }
        
        if (this.options.onInsert) {
            // Pasar también los estilos actuales para que se guarden con la fórmula
            const styles = {
                color: this.currentColor || '',
                size: this.currentSize || '',
                font: this.currentFont || '',
                fontStyle: this.currentFontStyle || 'normal'
            };
            this.options.onInsert(mathml, cleanLatex, styles);
        }
        
        // Cerrar el panel después de insertar
        this.close();
    }
    
    _toggleHandwritingMode() {
        this.isHandwritingMode = !this.isHandwritingMode;
        const panel = this.container.querySelector('.mathpanel');
        const btn = this.container.querySelector('.mathpanel-control-btn.handwriting');
        panel.classList.toggle('mathpanel-handwriting-mode', this.isHandwritingMode);
        btn.classList.toggle('active', this.isHandwritingMode);
        if (this.isHandwritingMode) {
            this._initSignaturePad();
            this._bindHandwritingEvents();
        }
    }
    
    _initSignaturePad() {
        if (this.signaturePad) return;
        this.canvas = this.container.querySelector('#mathpanel-canvas');
        const cont = this.canvas.parentElement;
        const rect = cont.getBoundingClientRect();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        this.canvas.width = rect.width * ratio;
        this.canvas.height = rect.height * ratio;
        this.canvas.style.width = rect.width + 'px';
        this.canvas.style.height = rect.height + 'px';
        this.canvas.getContext('2d').scale(ratio, ratio);
        
        this.signaturePad = new window.SignaturePad(this.canvas, {
            backgroundColor: 'rgb(255,255,255)',
            penColor: '#1a1a2e',
            minWidth: 1.5,
            maxWidth: 3
        });
        
        this.signaturePad.addEventListener('beginStroke', () => {
            this.container.querySelector('#mathpanel-canvas-placeholder')?.classList.add('hidden');
            this._updateRecognizeBtn();
        });
        this.signaturePad.addEventListener('endStroke', () => this._updateRecognizeBtn());
    }

    _bindHandwritingEvents() {
        if (this._hwBound) return;
        this._hwBound = true;
        
        this.container.querySelector('#mathpanel-hw-clear').addEventListener('click', () => {
            this.signaturePad.clear();
            this.container.querySelector('#mathpanel-canvas-placeholder')?.classList.remove('hidden');
            this._updateRecognizeBtn();
        });
        
        this.container.querySelector('#mathpanel-hw-undo').addEventListener('click', () => {
            const data = this.signaturePad.toData();
            if (data?.length) {
                data.pop();
                this.signaturePad.fromData(data);
                if (!data.length) this.container.querySelector('#mathpanel-canvas-placeholder')?.classList.remove('hidden');
                this._updateRecognizeBtn();
            }
        });
        
        this.container.querySelector('#mathpanel-hw-recognize').addEventListener('click', () => this._recognizeHandwriting());
    }
    
    _updateRecognizeBtn() {
        const btn = this.container.querySelector('#mathpanel-hw-recognize');
        if (btn && this.signaturePad) btn.disabled = this.signaturePad.isEmpty() || this.isRecognizing;
    }
    
    async _recognizeHandwriting() {
        if (!this.signaturePad || this.signaturePad.isEmpty() || this.isRecognizing) return;
        if (!this.options.openaiApiKey) {
            alert('Se requiere API key de OpenAI');
            return;
        }
        
        this.isRecognizing = true;
        const loading = this.container.querySelector('#mathpanel-hw-loading');
        const recognizeBtn = this.container.querySelector('#mathpanel-hw-recognize');
        loading?.classList.add('visible');
        if (recognizeBtn) recognizeBtn.style.display = 'none';
        
        try {
            const base64 = this.signaturePad.toDataURL('image/png').split(',')[1];
            const resp = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.options.openaiApiKey}` },
                body: JSON.stringify({
                    model: 'gpt-4.1-mini',
                    messages: [
                        { role: 'system', content: 'Convierte ecuaciones escritas a mano en LaTeX. Solo responde con el código LaTeX.' },
                        { role: 'user', content: [
                            { type: 'text', text: 'Convierte a LaTeX:' },
                            { type: 'image_url', image_url: { url: `data:image/png;base64,${base64}`, detail: 'high' } }
                        ]}
                    ],
                    max_tokens: 500,
                    temperature: 0.1
                })
            });
            
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            const latex = data.choices?.[0]?.message?.content?.trim();
            if (!latex || latex.startsWith('ERROR')) throw new Error(latex || 'Sin respuesta');
            
            this.mathfield.value = latex;
            this.signaturePad.clear();
            this.container.querySelector('#mathpanel-canvas-placeholder')?.classList.remove('hidden');
            this._toggleHandwritingMode();
        } catch (e) {
            alert('Error: ' + e.message);
        } finally {
            this.isRecognizing = false;
            loading?.classList.remove('visible');
            if (recognizeBtn) recognizeBtn.style.display = 'flex';
            this._updateRecognizeBtn();
        }
    }
    
    // === API Pública ===
    open(input = '', options = {}) {
        if (!this.container) this._createUI();
        this.container.style.display = 'block';
        this.isMinimized = false;
        this.container.classList.remove('minimized');
        
        // Resetear estilos a valores por defecto (o usar los proporcionados en options)
        this._resetStyles(options);
        
        if (input) {
            // Detectar si el input es MathML o LaTeX
            // Buscar <math o mathxmlns (sin espacio) que indica MathML pegado sin espacios
            const isMathML = /<math[^>]*>/i.test(input) || /mathxmlns=/i.test(input);
            
            let latex = '';
            if (isMathML) {
                // Es MathML, convertir a LaTeX primero
                try {
                    latex = this._convertMathMLToLatex(input) || '';
                } catch (e) {
                    console.warn('Error convirtiendo MathML a LaTeX:', e);
                    latex = '';
                }
            } else {
                // Es LaTeX
                latex = input;
            }
            
            // Limpiar placeholders para evitar cuadros negros al editar
            // Reemplazar con \;\; (dos espacios gruesos) para alinear con operadores
            latex = latex
                .replace(/\\placeholder\s*\{[^}]*\}/g, '\\;\\;')
                .replace(/\\placeholder/g, '\\;\\;')
                .replace(/\\phantom\s*\{[^}]*\}/g, '\\;\\;')
                .replace(/\\phantom/g, '\\;\\;')
                // Limpiar espacios múltiples
                .replace(/\\;\\;\s*\\;\\;/g, '\\;\\;');
            
            this.mathfield.value = latex;
        } else {
            this.mathfield.value = '';
        }
        
        setTimeout(() => this.mathfield.focus(), 100);
    }
    
    // Aplicar MathML del textarea y cambiar a modo visual
    _applyMathMLFromTextarea(mathmlInput, mathmlToggleBtn) {
        const mathmlCode = mathmlInput.value.trim();
        if (mathmlCode) {
            try {
                const latex = this._convertMathMLToLatex(mathmlCode);
                this.mathfield.value = latex || '';
            } catch (e) {
                console.warn('Error convirtiendo MathML:', e);
            }
        }
        mathmlInput.value = '';
        mathmlInput.style.display = 'none';
        this.mathfield.style.display = 'block';
        mathmlToggleBtn.classList.remove('active');
        mathmlToggleBtn.title = 'Cambiar a modo MathML';
        this.isMathMLMode = false;
        this.mathfield.focus();
    }
    
    // Convertir MathML a LaTeX
    _convertMathMLToLatex(mathml) {
        try {
            // Limpiar el MathML
            let cleanMathML = mathml
                .replace(/^\$\$\s*/g, '')
                .replace(/\s*\$\$$/g, '')
                // Normalizar saltos de línea y espacios múltiples
                .replace(/\r\n/g, ' ')
                .replace(/\r/g, ' ')
                .replace(/\n/g, ' ')
                .replace(/\s{2,}/g, ' ')
                // Arreglar espacios faltantes en atributos pegados
                .replace(/mathxmlns=/gi, 'math xmlns=')
                .replace(/mstylemathsize=/gi, 'mstyle mathsize=')
                .replace(/mtablecolumnalign=/gi, 'mtable columnalign=')
                .replace(/mstylefontweight=/gi, 'mstyle fontweight=')
                .replace(/mnmathvariant=/gi, 'mn mathvariant=')
                .replace(/momathvariant=/gi, 'mo mathvariant=')
                .replace(/mimathvariant=/gi, 'mi mathvariant=')
                .replace(/mtextmathvariant=/gi, 'mtext mathvariant=')
                .replace(/mrowmathcolor=/gi, 'mrow mathcolor=')
                .replace(/mstylemathcolor=/gi, 'mstyle mathcolor=')
                .replace(/mstylefontfamily=/gi, 'mstyle fontfamily=')
                // Arreglar atributos pegados genéricos: tag + atributo conocido
                .replace(/<(\w+)(mathvariant=)/gi, '<$1 $2')
                .replace(/<(\w+)(mathsize=)/gi, '<$1 $2')
                .replace(/<(\w+)(mathcolor=)/gi, '<$1 $2')
                .replace(/<(\w+)(fontfamily=)/gi, '<$1 $2')
                .replace(/<(\w+)(columnalign=)/gi, '<$1 $2')
                .replace(/<(\w+)(rowalign=)/gi, '<$1 $2')
                .replace(/<(\w+)(columnspacing=)/gi, '<$1 $2')
                .replace(/<(\w+)(rowspacing=)/gi, '<$1 $2')
                .trim();
            
            // Parsear como XML
            const parser = new DOMParser();
            const doc = parser.parseFromString(cleanMathML, 'text/xml');
            
            // Verificar errores de parseo
            const parseError = doc.querySelector('parsererror');
            if (parseError) {
                console.warn('Error parseando MathML, intentando con mathml-to-latex:', parseError.textContent);
                // Fallback a mathml-to-latex
                let latex = MathMLToLaTeX.convert(cleanMathML);
                return latex.replace(/^\$\$\s*/g, '').replace(/\s*\$\$$/g, '').trim();
            }
            
            // Extraer estilos globales del MathML (mstyle raíz)
            this._extractMathMLStyles(doc);
            
            // Convertir recursivamente
            const mathEl = doc.querySelector('math') || doc.documentElement;
            const latex = this._nodeToLatex(mathEl);
            
            return latex.replace(/^\$\$\s*/g, '').replace(/\s*\$\$$/g, '').trim();
        } catch (error) {
            console.error('Error en conversión MathML a LaTeX:', error);
            // Fallback a mathml-to-latex
            try {
                let latex = MathMLToLaTeX.convert(mathml);
                return latex.replace(/^\$\$\s*/g, '').replace(/\s*\$\$$/g, '').trim();
            } catch (e2) {
                return '';
            }
        }
    }
    
    // Extraer estilos globales del MathML y aplicarlos al panel
    _extractMathMLStyles(doc) {
        // Extraer font-family del atributo style de <math> (ej: style="font-family:'Courier New'")
        const mathEl = doc.querySelector('math');
        if (mathEl) {
            const styleAttr = mathEl.getAttribute('style') || '';
            const fontMatch = styleAttr.match(/font-family:\s*['"]?([^;'"]+)['"]?/i);
            if (fontMatch) {
                const fl = fontMatch[1].trim().toLowerCase();
                let detectedFont = '';
                if (fl.includes('courier') || fl.includes('monospace')) detectedFont = 'monospace';
                else if (fl.includes('sans-serif') || fl.includes('arial') || fl.includes('helvetica')) detectedFont = 'sans-serif';
                else if (fl.includes('serif') || fl.includes('times') || fl.includes('georgia')) detectedFont = 'serif';
                
                if (detectedFont) {
                    const fontSelect = this.container?.querySelector('#mathpanel-font-select');
                    if (fontSelect) {
                        const options = Array.from(fontSelect.options).map(o => o.value);
                        if (options.includes(detectedFont)) {
                            fontSelect.value = detectedFont;
                            this.currentFont = detectedFont;
                        }
                    }
                }
            }
        }
        
        const mstyle = doc.querySelector('mstyle');
        if (!mstyle) {
            this._updateMathfieldStyles();
            return;
        }
        
        // Extraer mathsize - mantener en pt para coincidir con las opciones del selector
        const mathsize = mstyle.getAttribute('mathsize');
        if (mathsize) {
            let sizePt = '';
            if (mathsize.endsWith('pt')) sizePt = mathsize;
            else if (mathsize.endsWith('px')) sizePt = Math.round(parseFloat(mathsize) / 1.333) + 'pt';
            else if (mathsize.endsWith('em') || mathsize.endsWith('rem')) sizePt = Math.round(parseFloat(mathsize) * 12) + 'pt';
            else if (!isNaN(parseFloat(mathsize))) sizePt = Math.round(parseFloat(mathsize) / 1.333) + 'pt';

            if (sizePt) {
                const sizeSelect = this.container?.querySelector('#mathpanel-size-select');
                if (sizeSelect) {
                    const options = Array.from(sizeSelect.options).map(o => o.value);
                    if (options.includes(sizePt)) {
                        sizeSelect.value = sizePt;
                        this.currentSize = sizePt;
                    }
                }
            }
        }
        
        // Extraer mathcolor
        const mathcolor = mstyle.getAttribute('mathcolor');
        if (mathcolor) {
            const colorSelect = this.container?.querySelector('#mathpanel-color-select');
            if (colorSelect) {
                const options = Array.from(colorSelect.options).map(o => o.value);
                if (options.includes(mathcolor)) {
                    colorSelect.value = mathcolor;
                    this.currentColor = mathcolor;
                }
            }
        }
        
        // Extraer fontfamily / mathvariant para detectar fuente
        const fontfamily = mstyle.getAttribute('fontfamily') || mstyle.getAttribute('font-family');
        const mathvariant = mstyle.getAttribute('mathvariant');
        let detectedFont = '';
        
        if (fontfamily) {
            const fl = fontfamily.toLowerCase();
            if (fl.includes('sans-serif') || fl.includes('arial') || fl.includes('helvetica')) {
                detectedFont = 'sans-serif';
            } else if (fl.includes('monospace') || fl.includes('courier')) {
                detectedFont = 'monospace';
            } else if (fl.includes('serif') || fl.includes('times')) {
                detectedFont = 'serif';
            }
        } else if (mathvariant) {
            if (mathvariant.includes('sans-serif')) detectedFont = 'sans-serif';
            else if (mathvariant === 'monospace') detectedFont = 'monospace';
        }
        
        if (detectedFont) {
            const fontSelect = this.container?.querySelector('#mathpanel-font-select');
            if (fontSelect) {
                const options = Array.from(fontSelect.options).map(o => o.value);
                if (options.includes(detectedFont)) {
                    fontSelect.value = detectedFont;
                    this.currentFont = detectedFont;
                }
            }
        }
        
        this._updateMathfieldStyles();
    }
    
    // Convertir un nodo MathML a LaTeX recursivamente
    _nodeToLatex(node) {
        if (!node) return '';
        
        // Nodo de texto
        if (node.nodeType === 3) {
            return node.textContent.trim();
        }
        
        // Nodo de elemento
        if (node.nodeType !== 1) return '';
        
        const tag = node.tagName?.toLowerCase() || '';
        const children = Array.from(node.childNodes);
        
        // Obtener atributos de estilo
        const mathvariant = node.getAttribute('mathvariant');
        const mathcolor = node.getAttribute('mathcolor');
        
        // Función helper para envolver con estilos
        const wrapStyle = (content) => {
            let result = content;
            if (mathvariant === 'bold' || mathvariant === 'bold-italic') {
                result = `\\mathbf{${result}}`;
            } else if (mathvariant === 'italic') {
                result = `\\mathit{${result}}`;
            } else if (mathvariant === 'double-struck') {
                result = `\\mathbb{${result}}`;
            } else if (mathvariant === 'script') {
                result = `\\mathcal{${result}}`;
            } else if (mathvariant === 'fraktur') {
                result = `\\mathfrak{${result}}`;
            } else if (mathvariant === 'monospace') {
                result = `\\mathtt{${result}}`;
            } else if (mathvariant === 'sans-serif') {
                result = `\\mathsf{${result}}`;
            }
            if (mathcolor && mathcolor !== '#000000' && mathcolor !== 'black') {
                result = `\\textcolor{${mathcolor}}{${result}}`;
            }
            return result;
        };
        
        // Procesar hijos como LaTeX
        const childLatex = () => children.map(c => this._nodeToLatex(c)).join('');
        
        switch (tag) {
            case 'math':
            case 'mrow':
            case 'semantics':
                return wrapStyle(childLatex());
                
            case 'mstyle': {
                // mstyle: los estilos ya se extrajeron globalmente, procesar hijos
                return wrapStyle(childLatex());
            }
                
            case 'mn': // número
                return wrapStyle(node.textContent.trim());
                
            case 'mi': { // identificador
                const text = node.textContent.trim();
                return wrapStyle(text);
            }
                
            case 'mo': { // operador
                const op = node.textContent.trim();
                // Mapear operadores especiales
                const opMap = {
                    '×': '\\times', '÷': '\\div', '±': '\\pm', '∓': '\\mp',
                    '≤': '\\leq', '≥': '\\geq', '≠': '\\neq', '≈': '\\approx',
                    '∞': '\\infty', '→': '\\rightarrow', '←': '\\leftarrow',
                    '⇒': '\\Rightarrow', '⇐': '\\Leftarrow', '∈': '\\in',
                    '∉': '\\notin', '⊂': '\\subset', '⊃': '\\supset',
                    '∪': '\\cup', '∩': '\\cap', '∧': '\\wedge', '∨': '\\vee',
                    '¬': '\\neg', '∀': '\\forall', '∃': '\\exists',
                    '□': '\\square', '△': '\\triangle',
                    '·': '\\cdot', '…': '\\ldots', '⋯': '\\cdots',
                    '∑': '\\sum', '∏': '\\prod', '∫': '\\int',
                };
                return opMap[op] || op;
            }
                
            case 'mtext': { // texto
                const text = node.textContent.trim();
                if (!text) return '';
                return wrapStyle(`\\text{${text}}`);
            }
                
            case 'mspace': {
                // Detectar salto de línea
                const linebreak = node.getAttribute('linebreak');
                if (linebreak === 'newline') return ' \\newline ';
                
                const width = node.getAttribute('width');
                if (width) {
                    const val = parseFloat(width);
                    if (val >= 1) return '\\quad';
                    if (val >= 0.5) return '\\;';
                    if (val >= 0.2) return '\\,';
                }
                return '\\,';
            }
                
            case 'mfrac': {
                const nums = children.filter(c => c.nodeType === 1);
                const num = nums[0] ? this._nodeToLatex(nums[0]) : '';
                const den = nums[1] ? this._nodeToLatex(nums[1]) : '';
                return wrapStyle(`\\frac{${num}}{${den}}`);
            }
                
            case 'msqrt':
                return wrapStyle(`\\sqrt{${childLatex()}}`);
                
            case 'mroot': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const index = parts[1] ? this._nodeToLatex(parts[1]) : '';
                return wrapStyle(`\\sqrt[${index}]{${base}}`);
            }
                
            case 'msup': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const sup = parts[1] ? this._nodeToLatex(parts[1]) : '';
                return `${base}^{${sup}}`;
            }
                
            case 'msub': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const sub = parts[1] ? this._nodeToLatex(parts[1]) : '';
                return `${base}_{${sub}}`;
            }
                
            case 'msubsup': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const sub = parts[1] ? this._nodeToLatex(parts[1]) : '';
                const sup = parts[2] ? this._nodeToLatex(parts[2]) : '';
                return `${base}_{${sub}}^{${sup}}`;
            }
                
            case 'mover': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const over = parts[1] ? this._nodeToLatex(parts[1]) : '';
                // Detectar acentos comunes
                if (over === '¯' || over === '‾') return `\\overline{${base}}`;
                if (over === '→') return `\\overrightarrow{${base}}`;
                if (over === '~' || over === '˜') return `\\tilde{${base}}`;
                if (over === '^' || over === '̂') return `\\hat{${base}}`;
                if (over === '˙') return `\\dot{${base}}`;
                return `\\overset{${over}}{${base}}`;
            }
                
            case 'munder': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const under = parts[1] ? this._nodeToLatex(parts[1]) : '';
                if (under === '_' || under === '‾') return `\\underline{${base}}`;
                return `\\underset{${under}}{${base}}`;
            }
                
            case 'munderover': {
                const parts = children.filter(c => c.nodeType === 1);
                const base = parts[0] ? this._nodeToLatex(parts[0]) : '';
                const under = parts[1] ? this._nodeToLatex(parts[1]) : '';
                const over = parts[2] ? this._nodeToLatex(parts[2]) : '';
                return `${base}_{${under}}^{${over}}`;
            }
                
            case 'menclose': {
                const notation = node.getAttribute('notation') || '';
                const content = childLatex();
                if (notation.includes('horizontalstrike')) return `\\cancel{${content}}`;
                if (notation.includes('box') || notation.includes('roundedbox')) return `\\boxed{${content}}`;
                return content;
            }
                
            case 'mphantom':
                return `\\phantom{${childLatex()}}`;
                
            case 'mpadded':
                return childLatex();
                
            case 'mtable':
                return this._parseTableNode(node);
                
            case 'annotation':
            case 'annotation-xml':
                return ''; // Ignorar anotaciones
                
            default:
                return childLatex();
        }
    }
    
    // Convertir contenido de una celda mtd, manejando mspace linebreak="newline"
    _parseCellContent(cell) {
        const children = Array.from(cell.childNodes);
        
        // Verificar si hay mspace con linebreak="newline" como hijo directo
        const hasNewline = children.some(c => 
            c.nodeType === 1 && c.tagName?.toLowerCase() === 'mspace' && c.getAttribute('linebreak') === 'newline'
        );
        
        if (!hasNewline) {
            return this._nodeToLatex(cell);
        }
        
        // Dividir el contenido en líneas separadas por mspace linebreak="newline"
        const lines = [];
        let currentLine = [];
        
        children.forEach(child => {
            if (child.nodeType === 1 && child.tagName?.toLowerCase() === 'mspace' && child.getAttribute('linebreak') === 'newline') {
                // Fin de línea actual
                if (currentLine.length > 0) {
                    lines.push(currentLine.map(c => this._nodeToLatex(c)).join(''));
                    currentLine = [];
                }
            } else {
                currentLine.push(child);
            }
        });
        // Última línea
        if (currentLine.length > 0) {
            lines.push(currentLine.map(c => this._nodeToLatex(c)).join(''));
        }
        
        if (lines.length <= 1) {
            return lines[0] || '';
        }
        
        // Envolver múltiples líneas en un array vertical
        return `\\begin{array}{l} ${lines.join(' \\\\ ')} \\end{array}`;
    }
    
    // Parser para nodos mtable
    _parseTableNode(mtable) {
        if (!mtable) return '';
        
        const rows = Array.from(mtable.children).filter(c => 
            c.tagName?.toLowerCase() === 'mtr' || c.tagName?.toLowerCase() === 'mlabeledtr'
        );
        if (rows.length === 0) return '';
        
        // Contar columnas
        const firstRow = rows[0];
        const colCount = Array.from(firstRow.children).filter(c => c.tagName?.toLowerCase() === 'mtd').length;
        
        // Obtener alineación
        const columnalign = mtable.getAttribute('columnalign') || '';
        let alignment = '';
        if (columnalign) {
            alignment = columnalign.split(/\s+/).map(a => {
                if (a === 'left') return 'l';
                if (a === 'right') return 'r';
                return 'c';
            }).join('');
        }
        // Completar alineación si faltan columnas
        while (alignment.length < colCount) alignment += 'r';
        
        // Generar filas
        const latexRows = [];
        rows.forEach(row => {
            const cells = Array.from(row.children).filter(c => c.tagName?.toLowerCase() === 'mtd');
            const cellContents = [];
            
            cells.forEach(cell => {
                // Convertir celda con soporte para newlines internos
                const content = this._parseCellContent(cell);
                cellContents.push(content || '');
            });
            
            latexRows.push(cellContents.join(' & '));
        });
        
        return `\\begin{array}{${alignment}} ${latexRows.join(' \\\\ ')} \\end{array}`;
    }
    
    // Resetear estilos a valores por defecto o a los proporcionados
    _resetStyles(options = {}) {
        // Valores por defecto: negro, 14pt, monospace, normal
        const defaultColor = '#000000';
        const defaultSize = this.options.mathsize || '14pt';
        const defaultFont = 'monospace';
        const defaultFontStyle = 'normal';
        
        // Usar valores de options si se proporcionan, sino usar defaults
        this.currentColor = options.color !== undefined ? options.color : defaultColor;
        this.currentSize = options.size !== undefined ? options.size : defaultSize;
        this.currentFont = options.font !== undefined ? options.font : defaultFont;
        this.currentFontStyle = options.fontStyle !== undefined ? options.fontStyle : defaultFontStyle;
        
        // Actualizar los selectores en la UI
        const colorSelect = this.container?.querySelector('#mathpanel-color-select');
        const sizeSelect = this.container?.querySelector('#mathpanel-size-select');
        const fontSelect = this.container?.querySelector('#mathpanel-font-select');
        const fontStyleSelect = this.container?.querySelector('#mathpanel-fontstyle-select');
        
        if (colorSelect) colorSelect.value = this.currentColor;
        if (sizeSelect) sizeSelect.value = this.currentSize;
        if (fontSelect) fontSelect.value = this.currentFont;
        if (fontStyleSelect) fontStyleSelect.value = this.currentFontStyle;
        
        // Aplicar estilos al mathfield
        this._updateMathfieldStyles();
    }
    
    openWithMathML(mathml, options = {}) {
        if (!this.container) this._createUI();
        this.container.style.display = 'block';
        this.isMinimized = false;
        this.container.classList.remove('minimized');
        
        // Resetear estilos a valores por defecto (o usar los proporcionados en options)
        this._resetStyles(options);
        
        if (mathml) {
            try {
                const latex = this._convertMathMLToLatex(mathml);
                this.mathfield.value = latex || '';
            } catch (e) {
                console.warn('Error cargando MathML:', e);
                this.mathfield.value = '';
            }
        }
        setTimeout(() => this.mathfield.focus(), 100);
    }
    
    close() {
        if (this.container) this.container.style.display = 'none';
        this.options.onClose?.();
    }
    
    toggleMinimize() {
        this.isMinimized = !this.isMinimized;
        this.container.classList.toggle('minimized', this.isMinimized);
        if (this.isMinimized) {
            Object.assign(this.container.style, { top: 'auto', left: 'auto', right: '16px', bottom: '20px' });
        }
    }
    
    clear() {
        if (this.mathfield) this.mathfield.value = '';
    }
    
    getValue() { return this.mathfield?.value || ''; }
    
    getMathML() {
        try { return this.mathfield?.getValue('math-ml') || ''; }
        catch { return ''; }
    }
    
    setValue(latex) { if (this.mathfield) this.mathfield.value = latex; }
    
    setMathML(mathml) {
        if (this.mathfield && mathml) {
            try { this.mathfield.setValue(mathml, { format: 'math-ml' }); }
            catch (e) { console.warn('Error:', e); }
        }
    }
    
    setOpenAIKey(key) { this.options.openaiApiKey = key; }
    
    // Métodos para establecer estilos
    setColor(color) { 
        this.currentColor = color;
        if (this.container) {
            this.container.querySelectorAll('.mathpanel-color-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.color === color);
            });
            this._updateMathfieldStyles();
        }
    }
    
    setSize(size) {
        this.currentSize = size;
        if (this.container) {
            const select = this.container.querySelector('#mathpanel-size-select');
            if (select) select.value = size;
            this._updateMathfieldStyles();
        }
    }
    
    setFont(font) {
        this.currentFont = font;
        if (this.container) {
            const select = this.container.querySelector('#mathpanel-font-select');
            if (select) select.value = font;
            this._updateMathfieldStyles();
        }
    }
    
    getStyles() {
        return {
            color: this.currentColor,
            size: this.currentSize,
            font: this.currentFont
        };
    }
    
    destroy() { 
        this.container?.remove(); 
        this.container = null; 
    }
    
    // === Métodos Estáticos ===
    static formatMathML(mathml, options = {}) {
        if (!mathml) return '';
        
        const { mathsize, mathcolor, mathfont, mathfontstyle } = options;
        
        mathml = mathml.replace(/<semantics>|<\/semantics>/g, '');
        mathml = mathml.replace(/<annotation[^>]*>[\s\S]*?<\/annotation>/g, '');
        
        // Determinar mathvariant basado en estilo de fuente
        let mathvariant = '';
        if (mathfontstyle === 'bold') {
            mathvariant = 'bold';
        } else if (mathfontstyle === 'italic') {
            mathvariant = 'italic';
        } else if (mathfont === 'sans-serif') {
            mathvariant = 'sans-serif';
        } else if (mathfont === 'monospace') {
            mathvariant = 'monospace';
        }
        
        // Limpiar mathvariant existentes para evitar acumulación de estilos
        // Solo mantener un estilo global
        mathml = mathml.replace(/\s*mathvariant="[^"]*"/g, '');
        
        // Aplicar mathvariant directamente a elementos mi y mn (no se hereda bien con mstyle)
        if (mathvariant && mathvariant !== 'normal') {
            // Agregar mathvariant a <mi>
            mathml = mathml.replace(/<mi([^>]*)>/g, `<mi mathvariant="${mathvariant}"$1>`);
            // Agregar mathvariant a <mn>
            mathml = mathml.replace(/<mn([^>]*)>/g, `<mn mathvariant="${mathvariant}"$1>`);
            // Agregar mathvariant a <mo> para operadores
            mathml = mathml.replace(/<mo([^>]*)>/g, `<mo mathvariant="${mathvariant}"$1>`);
        }
        
        // Aplicar color y tamaño usando mstyle (estos sí se heredan bien)
        if (mathsize || mathcolor) {
            const styleAttrs = [];
            if (mathsize) styleAttrs.push(`mathsize="${mathsize}"`);
            if (mathcolor) styleAttrs.push(`mathcolor="${mathcolor}"`);
            
            if (styleAttrs.length > 0) {
                const styles = styleAttrs.join(' ');
                
                if (mathml.includes('<math')) {
                    mathml = mathml.replace(/<math([^>]*)>/, `<math$1><mstyle ${styles}>`);
                    mathml = mathml.replace(/<\/math>/, '</mstyle></math>');
                } else {
                    mathml = `<mstyle ${styles}>${mathml}</mstyle>`;
                }
            }
        }
        
        if (!mathml.trim().startsWith('<math')) {
            mathml = `<math xmlns="http://www.w3.org/1998/Math/MathML">${mathml}</math>`;
        } else if (!mathml.includes('xmlns=')) {
            mathml = mathml.replace('<math', '<math xmlns="http://www.w3.org/1998/Math/MathML"');
        }
        
        return mathml.trim();
    }
}

export default MathPanel;
